import React, { useState, useEffect } from 'react';
import { AlertTriangle, Key, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Modal } from '@shared/components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';

interface PasswordChangePromptProps {
  isOpen: boolean;
  onClose: () => void;
  onPasswordChanged: () => void;
}

const PasswordChangePrompt: React.FC<PasswordChangePromptProps> = ({
  isOpen,
  onClose,
  onPasswordChanged
}) => {
  const { user } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  const passwordStrength = (): { strength: string; color: string; score: number } => {
    if (newPassword.length === 0) return { strength: 'Empty', color: 'bg-gray-300', score: 0 };
    if (newPassword.length < 6) return { strength: 'Too Short', color: 'bg-red-500', score: 1 };
    
    let score = 0;
    if (newPassword.length >= 8) score += 1;
    if (/[A-Z]/.test(newPassword)) score += 1;
    if (/[0-9]/.test(newPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(newPassword)) score += 1;
    
    if (score === 4) return { strength: 'Strong', color: 'bg-green-500', score: 4 };
    if (score >= 2) return { strength: 'Medium', color: 'bg-yellow-500', score: score };
    return { strength: 'Weak', color: 'bg-red-500', score: score };
  };

  const { strength, color, score } = passwordStrength();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (score < 2) {
      toast.error('Please choose a stronger password');
      return;
    }

    setIsChanging(true);

    try {
      // Update password in Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw updateError;
      }

      // Mark password as changed in profile
      const { error: markError } = await supabase.rpc('mark_password_changed', {
        user_id: user?.id
      });

      if (markError) {
        console.warn('Failed to mark password as changed:', markError);
        // Don't fail the process for this
      }

      toast.success('Password changed successfully! You can now use FutureLens normally.');
      onPasswordChanged();
      onClose();
    } catch (error: any) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Failed to change password');
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Password Change Required"
      size="md"
    >
      <div className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Security Requirement</h4>
              <p className="text-amber-700 text-sm mt-1">
                You're using a temporary password. For your security, please create a new password 
                before continuing to use FutureLens.
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password *
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                required
                disabled={isChanging}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            
            {newPassword && (
              <div className="mt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600">Password strength:</span>
                  <span className="text-xs font-medium">{strength}</span>
                </div>
                <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${color} transition-all duration-300`} 
                    style={{ width: `${(score / 4) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password *
            </label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              required
              disabled={isChanging}
            />
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-red-600 text-xs mt-1">Passwords do not match</p>
            )}
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h5 className="font-medium text-blue-800 mb-2">Password Requirements:</h5>
            <ul className="text-blue-700 text-sm space-y-1">
              <li className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${newPassword.length >= 6 ? 'text-green-500' : 'text-gray-300'}`} />
                At least 6 characters
              </li>
              <li className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/[A-Z]/.test(newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                At least one uppercase letter (recommended)
              </li>
              <li className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/[0-9]/.test(newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                At least one number (recommended)
              </li>
              <li className="flex items-center">
                <CheckCircle className={`h-3 w-3 mr-2 ${/[^A-Za-z0-9]/.test(newPassword) ? 'text-green-500' : 'text-gray-300'}`} />
                At least one special character (recommended)
              </li>
            </ul>
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              type="submit"
              disabled={isChanging || newPassword !== confirmPassword || newPassword.length < 6}
              className="w-full"
            >
              {isChanging ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-r-transparent mr-2" />
                  Changing Password...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Change Password & Continue
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PasswordChangePrompt;